import React, { useMemo } from 'react';
import {
  Checkbox,
  useTheme,
  Box,
  Stack,
  Typography,
  TextField
} from '@mui/material';

import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';

import { useFiltersByName } from 'src/hooks/useFiltersByName';
import { useUnlimitedScroll } from 'src/hooks/useUnlimitedScroll';
import InfiniteScroll from 'react-infinite-scroll-component';

import { IEcommerceSubBrandHierarchyWithColor } from './EditItemComparison';

interface TableProps {
  dataSubBrand: IEcommerceSubBrandHierarchyWithColor[];
  setDataSubBrand: React.Dispatch<
    React.SetStateAction<IEcommerceSubBrandHierarchyWithColor[]>
  >;
  selectedSubBrands: IEcommerceSubBrandHierarchyWithColor[];
  setSelectedSubBrands: React.Dispatch<
    React.SetStateAction<IEcommerceSubBrandHierarchyWithColor[]>
  >;
}

const TableSubBrand: React.FC<TableProps> = ({
  dataSubBrand,
  setDataSubBrand,
  selectedSubBrands,
  setSelectedSubBrands
}) => {
  const theme = useTheme();

  // Check if all products in a subbrand are selected
  const isBrandFullySelected = (
    subbrand: IEcommerceSubBrandHierarchyWithColor
  ) => {
    return selectedSubBrands.some((item) => item.id === subbrand.id);
  };

  // Handle subbrand checkbox change
  const handleBrandCheckChange = (
    subbrand: IEcommerceSubBrandHierarchyWithColor,
    checked: boolean
  ) => {
    if (checked) {
      // Add all products from subbrand
      setSelectedSubBrands([...selectedSubBrands, subbrand]);
    } else {
      // Remove all products from subbrand
      setSelectedSubBrands(
        selectedSubBrands.filter((item) => item.id !== subbrand.id)
      );
    }
  };

  // Handle select all checkbox change
  const handleSelectAllChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (event.target.checked) {
      // Select all products
      setSelectedSubBrands(dataSubBrand);
    } else {
      // Clear all selections
      setSelectedSubBrands([]);
    }
  };

  const addName = useMemo((): Array<IEcommerceSubBrandHierarchyWithColor> => {
    return dataSubBrand.map((item) => ({
      ...item,
      name: item.name
    }));
  }, [dataSubBrand]);

  const { filteredList, handleFilter } =
    useFiltersByName<IEcommerceSubBrandHierarchyWithColor>(addName);

  const { fetchMoreData, infiniteScroll } =
    useUnlimitedScroll<IEcommerceSubBrandHierarchyWithColor>(filteredList);

  const handleChangeColorBrand = (
    subbrand: IEcommerceSubBrandHierarchyWithColor,
    color: string
  ) => {
    setDataSubBrand(
      dataSubBrand.map((item) => {
        return {
          ...item,
          color: item.id === subbrand.id ? color : item.color
        };
      })
    );
    setSelectedSubBrands(
      selectedSubBrands.map((item) => {
        return {
          ...item,
          color: item.id === subbrand.id ? color : item.color
        };
      })
    );
  };

  const handleChangeInputColorBrand = (
    subbrand: IEcommerceSubBrandHierarchyWithColor
  ) => {
    setDataSubBrand(
      dataSubBrand.map((item) => {
        return {
          ...item,
          isInputColor:
            item.id === subbrand.id ? !item.isInputColor : item.isInputColor
        };
      })
    );
    setSelectedSubBrands(
      selectedSubBrands.map((item) => {
        return {
          ...item,
          isInputColor:
            item.id === subbrand.id ? !item.isInputColor : item.isInputColor
        };
      })
    );
  };

  return (
    <>
      <Box
        sx={{
          border: `1px solid ${theme.palette.grey[300]}`,
          p: theme.spacing(1),
          borderRadius: theme.spacing(1)
        }}
      >
        <Typography fontSize="16px" fontWeight={700}>
          Sub-brand
        </Typography>
        <Typography fontSize="14px" color={theme.palette.grey[500]}>
          Select specific sub-brand objects and set chart color.
        </Typography>
      </Box>
      <Box
        sx={{
          border: `1px solid ${theme.palette.grey[300]}`,
          p: theme.spacing(1),
          borderRadius: theme.spacing(1)
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
        >
          <TextField
            name="keyword"
            variant="outlined"
            size="small"
            autoComplete="off"
            placeholder="Search Sub-brand"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: 'white',
                fontSize: '14px'
              }
            }}
            onChange={(event) => handleFilter(event.target.value)}
          />
          <Stack direction="row" alignItems="center" justifyContent="center">
            <Checkbox
              checked={dataSubBrand.length === selectedSubBrands.length}
              indeterminate={
                selectedSubBrands.length > 0 &&
                dataSubBrand.length !== selectedSubBrands.length
              }
              size="small"
              onChange={handleSelectAllChange}
              indeterminateIcon={
                <IndeterminateCheckBoxIcon
                  sx={{
                    width: theme.spacing(2.5),
                    height: theme.spacing(2.5)
                  }}
                />
              }
            />
            <Typography fontSize="14px" color={theme.palette.grey[500]}>
              Select All Sub-brand
            </Typography>
          </Stack>
        </Stack>
        <InfiniteScroll
          dataLength={infiniteScroll.list.length}
          next={fetchMoreData}
          hasMore={infiniteScroll.hasMore}
          loader={<div />}
          height={250}
          endMessage={<div />}
        >
          {infiniteScroll.list.map((subbrand) => (
            <Box
              key={subbrand.id}
              sx={{
                border: `1px solid ${theme.palette.grey[300]}`,
                p: theme.spacing(1),
                borderRadius: theme.spacing(1),
                my: theme.spacing(1)
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                width="100%"
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Checkbox
                    checked={isBrandFullySelected(subbrand)}
                    onChange={(e) =>
                      handleBrandCheckChange(subbrand, e.target.checked)
                    }
                  />
                  <Box
                    sx={{
                      width: '10px',
                      height: '10px',
                      backgroundColor: subbrand.color,
                      borderRadius: '100%'
                    }}
                  />
                  <Typography fontSize="14px" fontWeight={700}>
                    {subbrand.name}
                  </Typography>
                </Stack>
                {/* <Stack>
                  <Box
                    sx={{
                      border: `1px solid ${theme.palette.grey[500]}`,
                      p: theme.spacing(1),
                      borderRadius: theme.spacing(1),
                      cursor: 'pointer'
                    }}
                    onClick={() => handleChangeInputColorBrand(subbrand)}
                  >
                    <Box
                      sx={{
                        width: '12px',
                        height: '12px',
                        backgroundColor: subbrand.color,
                        borderRadius: '100%'
                      }}
                    />
                  </Box>
                  {subbrand.isInputColor ? (
                    <div
                      style={{
                        position: 'absolute',
                        zIndex: '5'
                      }}
                    >
                      <div
                        style={{
                          position: 'fixed',
                          top: '0px',
                          right: '0px',
                          bottom: '0px',
                          left: '0px'
                        }}
                        onClick={() => handleChangeInputColorBrand(subbrand)}
                      />
                      <SketchPicker
                        color={subbrand.color}
                        onChangeComplete={(color) =>
                          handleChangeColorBrand(subbrand, color.hex)
                        }
                      />
                    </div>
                  ) : null}
                </Stack> */}
              </Stack>
            </Box>
          ))}
        </InfiniteScroll>
      </Box>
    </>
  );
};

export default TableSubBrand;
